'use client';

import { clearInstantRouteTransition } from '@/lib/instantRouteNavStore';
import ListPageLoadingShell from '@/components/ListPageLoadingShell';
import ProductLoadingSkeleton from '@/components/ProductLoadingSkeleton';
import { getInstantRouteTransitionKind, isStaleRenderedRoute } from '@/lib/instantRouteTransition';
import { usePathname } from 'next/navigation';
import { useEffect, Fragment, type ReactNode } from 'react';

/**
 * Next soft-nav updates the address bar before the new server tree commits.
 * Without this, the previous category H1/products stay on screen under the new URL.
 *
 * `contentPath` must come from the RSC that rendered this tree (layout/page params),
 * not from useParams() — those follow the URL immediately.
 */
export default function StaleRouteGuard({
  contentPath,
  children,
}: {
  contentPath: string;
  children: ReactNode;
}) {
  const pathname = usePathname() || '/';
  const stale = isStaleRenderedRoute(contentPath, pathname);

  useEffect(() => {
    if (stale) return;
    clearInstantRouteTransition();
  }, [stale, contentPath, pathname]);

  if (stale) {
    const kind = getInstantRouteTransitionKind(pathname);
    // Never keep painting the previous route (logo → home used to leave /celenk
    // on screen because `/` had no listing skeleton).
    if (kind === 'product') {
      return (
        <div className="px-2 md:px-4 mt-4 mb-16 bg-hzc_beige_200">
          <ProductLoadingSkeleton />
        </div>
      );
    }
    if (kind === 'list') {
      return <ListPageLoadingShell />;
    }
    return <div className="min-h-[70vh] bg-hzc_beige_200" aria-busy="true" />;
  }

  return <Fragment key={contentPath}>{children}</Fragment>;
}
